import { YearbookElement, PageBackground, CornerRadii, ElementShadow } from '@/types/yearbook';

export interface ApiTemplateObject {
  trame_background_id?: number;
  x: number;
  y: number;
  width: number;
  height: number;
  object_type: 'picture_template' | 'picture' | 'free_slot' | 'text' | 'text_template' | 'background' | 'background_color' | 'sticker' | 'template_sticker' | 'shape' | 'shape_border';
  angle?: number;
  content?: string;
  color?: string;
  fontSize?: string;
  fontFamily?: string;
  fontWeight?: string;
  justify?: string;
  image_url?: string;
  background_width?: number;
  background_height?: number;
  thumbnail?: string;
  picture_template_url?: string;
  bring_to_front?: string;
  send_to_back?: string;
  zIndex?: number | string;
  shape_type?: string;
  is_circle?: string;
  backgroundColor?: string;
  force_dimension?: boolean;
  picture_id?: number;
  user_id?: number;
  rounded?: boolean | string;
  corner_radii?: { tl?: number; tr?: number; br?: number; bl?: number } | string;
  border_color?: string;
  borderColor?: string;
  shadow?: { offsetX: number; offsetY: number; blur: number; color: string } | string;
}

export interface SlotDef {
  x: number;
  y: number;
  width: number;
  height: number;
  rotation?: number;
  previewImageUrl?: string;
  isCircle?: boolean;
  backgroundColor?: string;
  zPriority?: 'front' | 'back';
  zIndex?: number;
  cornerRadii?: CornerRadii;
  isFreeSlot?: boolean;
}

export interface TextTemplateDef {
  x: number;
  y: number;
  width: number;
  height: number;
  rotation: number;
  content: string;
  color: string;
  fontSize: number;
  fontFamily: string;
  fontWeight?: string;
  textAlign?: 'left' | 'center' | 'right';
  backgroundColor?: string;
  zPriority?: 'front' | 'back';
  zIndex?: number;
  cornerRadii?: CornerRadii;
}

export interface ExtraTemplateDef {
  x: number;
  y: number;
  width: number;
  height: number;
  rotation: number;
  type: 'sticker' | 'template_sticker' | 'shape' | 'shape_border';
  imageUrl?: string;
  color?: string;
  shapeType?: string;
  locked?: boolean;
  zPriority?: 'front' | 'back';
  zIndex?: number;
  backgroundColor?: string;
  forceDimension?: boolean;
  cornerRadii?: CornerRadii;
}

export interface LayoutTemplateDef {
  slots: SlotDef[];
  texts: TextTemplateDef[];
  extras: ExtraTemplateDef[];
  backgroundImageUrl?: string;
  backgroundColor?: string;
  trameBackgroundId?: number;
}

function mapJustifyToAlign(justify?: string): 'left' | 'center' | 'right' {
  if (justify === 'center') return 'center';
  if (justify === 'right') return 'right';
  return 'left';
}

function resolveZPriority(obj: ApiTemplateObject): 'front' | 'back' | undefined {
  if (obj.bring_to_front === 'true' || (obj.bring_to_front as unknown) === true) return 'front';
  if (obj.send_to_back === 'true' || (obj.send_to_back as unknown) === true) return 'back';
  return undefined;
}

function resolveZIndex(obj: ApiTemplateObject): number | undefined {
  if (obj.zIndex === undefined || obj.zIndex === null) return undefined;
  const val = typeof obj.zIndex === 'string' ? parseInt(obj.zIndex, 10) : obj.zIndex;
  return isNaN(val) ? undefined : val;
}

function resolveCornerRadii(obj: ApiTemplateObject): CornerRadii | undefined {
  if (!obj.corner_radii) return undefined;
  let cr: any = obj.corner_radii;
  if (typeof cr === 'string') {
    try { cr = JSON.parse(cr); } catch { return undefined; }
  }
  if (!cr.tl && !cr.tr && !cr.br && !cr.bl) return undefined;
  return cr as CornerRadii;
}

function resolveShadow(obj: ApiTemplateObject): ElementShadow | undefined {
  if (!obj.shadow) return undefined;
  let s: any = obj.shadow;
  if (typeof s === 'string') {
    try { s = JSON.parse(s); } catch { return undefined; }
  }
  if (s.offsetX == null || s.offsetY == null) return undefined;
  return s as ElementShadow;
}

function buildTextElement(obj: ApiTemplateObject, generateId: () => string): YearbookElement {
  return {
    id: generateId(),
    type: 'text',
    position: { x: obj.x ?? 0, y: obj.y ?? 0 },
    size: { width: obj.width ?? 200, height: obj.height ?? 40 },
    rotation: obj.angle ?? 0,
    content: obj.content ?? '',
    fromLayout: true,
    ...(obj.user_id !== undefined ? { userId: obj.user_id } : {}),
    thumbnail: obj.thumbnail,
    zPriority: resolveZPriority(obj),
    zIndex: resolveZIndex(obj),
    backgroundColor: obj.backgroundColor,
    textStyle: {
      fontSize: parseInt(obj.fontSize ?? '18', 10),
      fontFamily: obj.fontFamily ?? 'Arial',
      fontWeight: obj.fontWeight ?? '400',
      color: obj.color ?? '#000000',
      textAlign: mapJustifyToAlign(obj.justify),
      italic: false,
      underline: false,
    },
  };
}

/**
 * Parse API template objects into YearbookElements + optional background.
 */
export function parseTemplateObjects(
  objects: ApiTemplateObject[],
  generateId: () => string
): { elements: YearbookElement[]; background?: PageBackground } {
  const elements: YearbookElement[] = [];
  let background: PageBackground | undefined;

  for (const obj of objects) {
    const zPriority = resolveZPriority(obj);
    const zIdx = resolveZIndex(obj);

    if (obj.object_type === 'background' && obj.image_url) {
      background = { type: 'image', value: obj.image_url, trame_background_id: obj.trame_background_id ?? null };
    } else if (obj.object_type === 'background_color' && obj.color) {
      background = { type: 'color', value: obj.color };
    } else if (obj.object_type === 'picture_template') {
      console.log('[parseTemplate] picture_template angle:', obj.angle, typeof obj.angle, '→ rotation:', Number(obj.angle) || 0);
      elements.push({
        id: generateId(),
        type: 'slot',
        position: { x: obj.x ?? 0, y: obj.y ?? 0 },
        size: { width: obj.width ?? 200, height: obj.height ?? 150 },
        rotation: Number(obj.angle) || 0,
        fromLayout: true,
        thumbnail: obj.thumbnail,
        previewImageUrl: obj.picture_template_url,
        isCircle: obj.is_circle === 'true' || obj.rounded === true || obj.rounded === 'true',
        backgroundColor: obj.backgroundColor,
        zPriority,
        zIndex: zIdx,
        pictureId: obj.picture_id,
        ...(obj.user_id !== undefined ? { userId: obj.user_id } : {}),
        cornerRadii: resolveCornerRadii(obj),
        borderColor: obj.border_color || obj.borderColor,
        shadow: resolveShadow(obj),
      });
    } else if (obj.object_type === 'picture') {
      elements.push({
        id: generateId(),
        type: 'slot',
        position: { x: obj.x ?? 0, y: obj.y ?? 0 },
        size: { width: obj.width ?? 200, height: obj.height ?? 150 },
        rotation: Number(obj.angle) || 0,
        fromLayout: true,
        thumbnail: obj.thumbnail,
        isCircle: obj.is_circle === 'true' || obj.rounded === true || obj.rounded === 'true',
        backgroundColor: obj.backgroundColor,
        zPriority,
        zIndex: zIdx,
        ...(obj.image_url ? { imageUrl: obj.image_url } : {}),
        pictureId: obj.picture_id,
        ...(obj.user_id !== undefined ? { userId: obj.user_id } : {}),
        cornerRadii: resolveCornerRadii(obj),
        borderColor: obj.border_color || obj.borderColor,
        shadow: resolveShadow(obj),
      });
    } else if (obj.object_type === 'free_slot') {
      elements.push({
        id: generateId(),
        type: 'free_slot',
        position: { x: obj.x ?? 0, y: obj.y ?? 0 },
        size: { width: obj.width ?? 200, height: obj.height ?? 150 },
        rotation: Number(obj.angle) || 0,
        fromLayout: true,
        thumbnail: obj.thumbnail,
        previewImageUrl: obj.picture_template_url,
        isCircle: obj.is_circle === 'true' || obj.rounded === true || obj.rounded === 'true',
        backgroundColor: obj.backgroundColor,
        zPriority,
        zIndex: zIdx,
        cornerRadii: resolveCornerRadii(obj),
        borderColor: obj.border_color || obj.borderColor,
        shadow: resolveShadow(obj),
      });
    } else if (obj.object_type === 'text' || obj.object_type === 'text_template') {
      elements.push(buildTextElement(obj, generateId));
    } else if (obj.object_type === 'sticker') {
      elements.push({
        id: generateId(),
        type: 'image',
        position: { x: obj.x ?? 0, y: obj.y ?? 0 },
        size: { width: obj.width ?? 100, height: obj.height ?? 100 },
        rotation: obj.angle ?? 0,
        imageUrl: obj.thumbnail || obj.image_url || '',
        thumbnail: obj.thumbnail,
        fromLayout: true,
        backgroundColor: obj.backgroundColor,
        zPriority,
        zIndex: zIdx,
        forceDimension: obj.force_dimension === true || (obj.force_dimension as unknown) === 'true',
        borderColor: obj.border_color || obj.borderColor,
        shadow: resolveShadow(obj),
      });
    } else if (obj.object_type === 'template_sticker') {
      elements.push({
        id: generateId(),
        type: 'image',
        position: { x: obj.x ?? 0, y: obj.y ?? 0 },
        size: { width: obj.width ?? 100, height: obj.height ?? 100 },
        rotation: obj.angle ?? 0,
        imageUrl: obj.thumbnail || obj.image_url || '',
        thumbnail: obj.thumbnail,
        locked: true,
        fromLayout: true,
        backgroundColor: obj.backgroundColor,
        zPriority,
        zIndex: zIdx,
        forceDimension: obj.force_dimension === true || (obj.force_dimension as unknown) === 'true',
        borderColor: obj.border_color || obj.borderColor,
        shadow: resolveShadow(obj),
      });
    } else if (obj.object_type === 'shape') {
      elements.push({
        id: generateId(),
        type: 'shape',
        position: { x: obj.x ?? 0, y: obj.y ?? 0 },
        size: { width: obj.width ?? 100, height: obj.height ?? 100 },
        rotation: obj.angle ?? 0,
        shapeType: (obj.shape_type as 'rect' | 'line' | 'circle') || 'rect',
        textStyle: { fontSize: 0, fontFamily: '', fontWeight: '', color: obj.color ?? '#000000', textAlign: 'left', italic: false, underline: false },
        fromLayout: true,
        thumbnail: obj.thumbnail,
        zPriority,
        zIndex: zIdx,
        cornerRadii: resolveCornerRadii(obj),
        borderColor: obj.border_color || obj.borderColor,
        shadow: resolveShadow(obj),
      });
    } else if (obj.object_type === 'shape_border') {
      elements.push({
        id: generateId(),
        type: 'shape_border',
        position: { x: obj.x ?? 0, y: obj.y ?? 0 },
        size: { width: obj.width ?? 200, height: obj.height ?? 200 },
        rotation: obj.angle ?? 0,
        textStyle: { fontSize: 0, fontFamily: '', fontWeight: '', color: obj.color ?? '#000000', textAlign: 'left', italic: false, underline: false },
        fromLayout: true,
        thumbnail: obj.thumbnail,
        zPriority,
        zIndex: zIdx,
        cornerRadii: resolveCornerRadii(obj),
        borderColor: obj.border_color || obj.borderColor,
        shadow: resolveShadow(obj),
      });
    }
  }

  // Sort by zIndex (if provided), then by zPriority: back first, then normal, then front
  elements.sort((a, b) => {
    const orderP = (z?: 'front' | 'back') => (z === 'back' ? -1 : z === 'front' ? 1 : 0);
    const priorityA = a.zIndex ?? orderP(a.zPriority);
    const priorityB = b.zIndex ?? orderP(b.zPriority);
    return priorityA - priorityB;
  });

  return { elements, background };
}

/**
 * Parse API template objects into LayoutTemplateDef (slots + texts + extras).
 */
export function parseTemplateToLayoutDef(objects: ApiTemplateObject[]): LayoutTemplateDef {
  const slots: SlotDef[] = [];
  const texts: TextTemplateDef[] = [];
  const extras: ExtraTemplateDef[] = [];

  for (const obj of objects) {
    const zPriority = resolveZPriority(obj);
    const zIdx = resolveZIndex(obj);

    if (obj.object_type === 'picture_template') {
      const zp = resolveZPriority(obj);
      slots.push({
        x: obj.x,
        y: obj.y,
        width: obj.width,
        height: obj.height,
        rotation: obj.angle ?? 0,
        previewImageUrl: obj.picture_template_url,
        isCircle: obj.is_circle === 'true' || obj.rounded === true || obj.rounded === 'true',
        backgroundColor: obj.backgroundColor,
        zPriority: zp,
        zIndex: zIdx,
        cornerRadii: resolveCornerRadii(obj),
      });
    } else if (obj.object_type === 'text' || obj.object_type === 'text_template') {
      texts.push({
        x: obj.x,
        y: obj.y,
        width: obj.width,
        height: obj.height,
        rotation: obj.angle ?? 0,
        content: obj.content ?? '',
        color: obj.color ?? '#000000',
        fontSize: parseInt(obj.fontSize ?? '18', 10),
        fontFamily: obj.fontFamily ?? 'Arial',
        fontWeight: obj.fontWeight,
        textAlign: mapJustifyToAlign(obj.justify),
        backgroundColor: obj.backgroundColor,
        zPriority: resolveZPriority(obj),
        zIndex: zIdx,
      });
    } else if (obj.object_type === 'picture') {
      const zp = resolveZPriority(obj);
      slots.push({
        x: obj.x,
        y: obj.y,
        width: obj.width,
        height: obj.height,
        rotation: obj.angle ?? 0,
        previewImageUrl: obj.image_url || obj.picture_template_url,
        isCircle: obj.is_circle === 'true' || obj.rounded === true || obj.rounded === 'true',
        backgroundColor: obj.backgroundColor,
        zPriority: zp,
        zIndex: zIdx,
        cornerRadii: resolveCornerRadii(obj),
      });
    } else if (obj.object_type === 'free_slot') {
      // Free slots: render as slots in template previews, but flagged so applyLayout keeps the type
      slots.push({
        x: obj.x,
        y: obj.y,
        width: obj.width,
        height: obj.height,
        rotation: obj.angle ?? 0,
        previewImageUrl: obj.picture_template_url,
        isCircle: obj.is_circle === 'true' || obj.rounded === true || obj.rounded === 'true',
        backgroundColor: obj.backgroundColor,
        zPriority,
        zIndex: zIdx,
        cornerRadii: resolveCornerRadii(obj),
        isFreeSlot: true,
      });
    } else if (obj.object_type === 'sticker' || obj.object_type === 'template_sticker' || obj.object_type === 'shape' || obj.object_type === 'shape_border') {
      extras.push({
        x: obj.x,
        y: obj.y,
        width: obj.width,
        height: obj.height,
        rotation: obj.angle ?? 0,
        type: obj.object_type,
        imageUrl: obj.thumbnail || obj.image_url,
        color: obj.color,
        shapeType: obj.shape_type,
        locked: obj.object_type === 'template_sticker',
        zPriority,
        zIndex: zIdx,
        backgroundColor: obj.backgroundColor,
        forceDimension: obj.force_dimension === true || (obj.force_dimension as unknown) === 'true',
        cornerRadii: resolveCornerRadii(obj),
      });
    }
  }

  // Extract background color from background_color objects
  let backgroundColor: string | undefined;
  for (const obj of objects) {
    if (obj.object_type === 'background_color' && obj.color) {
      backgroundColor = obj.color;
    }
  }

  return { slots, texts, extras, backgroundColor };
}
